import {
  useCreateModifiableList,
  useDeleteModifiableList,
  useSaneModifiableList,
  useUpdateModifiableListItem,
  useUpdateModifiableListSequence,
} from "@/api/modifiableList";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/datatable";
import { FormControl, FormField, HeadlessFormItem } from "@/components/ui/form";
import { FormCol } from "@/components/ui/form-col";
import { Input } from "@/components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTrigger,
} from "@/components/ui/sheet";

import { useAuth } from "@/context/auth/AuthContext";
import { getMainLanguage, SupportedLocale } from "@/i18n";
import { ModifiableList as ModifiableListType } from "@/lib/types";
import { cn } from "@/lib/utils";
import { QuestionMarkCircledIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import {
  CheckIcon,
  LockIcon,
  PenIcon,
  PlusIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import { ComponentProps, useCallback, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Row, Text } from "./AbstractForm";

const ModifiableListContent = ({
  name,
  listKey,
  onSelectValue,
  lang,
}: {
  name: string;
  listKey: string;
  onSelectValue: (value: string) => void;
  lang?: SupportedLocale;
}) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [newItem, setNewItem] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  const modifiableListDeleter = useDeleteModifiableList(listKey);
  const modifiableListAdder = useCreateModifiableList(listKey);
  const modifiableListUpdater = useUpdateModifiableListItem(listKey);
  const modifiableListSequenceMutator =
    useUpdateModifiableListSequence(listKey);

  const { data } = useSaneModifiableList(listKey, lang ?? getMainLanguage());

  const lowerSearch = useMemo(() => search.toLowerCase(), [search]);

  const filteredData = useMemo(
    () =>
      lowerSearch.length > 0
        ? (data?.filter((d) => d.value.toLowerCase().includes(lowerSearch)) ??
          [])
        : (data ?? []),
    [data, lowerSearch],
  );

  const handleStartEdit = useCallback((id: string, currentValue: string) => {
    setEditingId(id);
    setEditValue(currentValue);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingId(null);
    setEditValue("");
  }, []);

  const handleSaveEdit = useCallback(
    (id: string) => {
      if (editValue.trim().length > 0) {
        modifiableListUpdater.mutate(
          { id, value: editValue.trim() },
          {
            onSuccess: () => {
              setEditingId(null);
              setEditValue("");
            },
          },
        );
      }
    },
    [editValue, modifiableListUpdater],
  );

  const columns: ColumnDef<ModifiableListType>[] = useMemo(() => {
    return [
      {
        id: name,
        size: 10000,
        cell: (val) => {
          const isEditing = editingId === val.row.original.id;
          const isLocked = val.row.original.isLocked ?? false;
          return isEditing ? (
            <Input
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleSaveEdit(val.row.original.id);
                } else if (e.key === "Escape") {
                  handleCancelEdit();
                }
              }}
              autoFocus
              className="text-wrap"
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <div className="flex items-center gap-2">
              <span className="text-wrap">{val.row.original.value}</span>
              {isLocked && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <LockIcon className="size-4 text-slate-500" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {t("itemLocked") ||
                          "This item is locked and cannot be modified"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        size: 0,
        cell: (val) => {
          const isEditing = editingId === val.row.original.id;
          const isLocked = val.row.original.isLocked ?? false;
          return (
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <Button
                    className="w-fit p-1"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSaveEdit(val.row.original.id);
                    }}
                  >
                    <CheckIcon className="size-4 stroke-slate-600" />
                  </Button>
                  <Button
                    className="w-fit p-1"
                    variant="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCancelEdit();
                    }}
                  >
                    <XIcon className="size-4 stroke-slate-600" />
                  </Button>
                </>
              ) : (
                <>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            className="w-fit p-1"
                            variant="ghost"
                            disabled={isLocked}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isLocked) {
                                modifiableListDeleter.mutate(
                                  val.row.original.id,
                                );
                              }
                            }}
                          >
                            <TrashIcon
                              className={cn(
                                "size-4 stroke-slate-600",
                                isLocked && "opacity-50",
                              )}
                            />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {isLocked && (
                        <TooltipContent>
                          <p>
                            {t("itemLocked") ||
                              "This item is locked and cannot be deleted"}
                          </p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Button
                            className="w-fit p-1"
                            variant="ghost"
                            disabled={isLocked}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isLocked) {
                                handleStartEdit(
                                  val.row.original.id,
                                  val.row.original.value,
                                );
                              }
                            }}
                          >
                            <PenIcon
                              className={cn(
                                "size-4 stroke-slate-600",
                                isLocked && "opacity-50",
                              )}
                            />
                          </Button>
                        </span>
                      </TooltipTrigger>
                      {isLocked && (
                        <TooltipContent>
                          <p>
                            {t("itemLocked") ||
                              "This item is locked and cannot be edited"}
                          </p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </>
              )}
            </div>
          );
        },
      },
    ];
  }, [
    name,
    modifiableListDeleter,
    editingId,
    editValue,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    t,
  ]);

  const handleRowDragged = async (newData: ModifiableListType[]) => {
    const newSequence = newData.map((d) => d.id);
    await modifiableListSequenceMutator.mutate({
      newSequence,
    });
  };

  return (
    <>
      <SheetHeader className="p-8 py-4 border-b"></SheetHeader>
      <div className="h-full p-8 gap-8 overflow-auto">
        <FormCol>
          <FormCol className="gap-0">
            <label
              htmlFor="addElementListModifiable"
              className="pb-1.5 text-sm"
            >
              {t("addElementListModifiable")}
            </label>
            <Row className="gap-2">
              <Input
                name="addElementListModifiable"
                id="addElementListModifiable"
                onChange={(e) => setNewItem(e.target.value)}
                value={newItem}
              />
              <Button
                className="p-2"
                variant="ghost"
                disabled={newItem.length <= 0}
                onClick={() => {
                  modifiableListAdder.mutate(
                    {
                      listKey: listKey,
                      value: newItem,
                      lang: lang ?? getMainLanguage(),
                    },
                    { onSuccess: () => setNewItem("") },
                  );
                }}
              >
                <PlusIcon className="size-4 stroke-slate-600" />
              </Button>
            </Row>
          </FormCol>

          <Input
            placeholder={t("search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <h2 className="font-semibold text-xl">{t(name)}</h2>
          <DataTable
            enableDragRows
            onRowDragged={(d) => handleRowDragged(d.newData)}
            slim
            headless
            data={filteredData}
            columns={columns}
            onRowClick={(value) => onSelectValue(value.original.value)}
          />
        </FormCol>
      </div>
    </>
  );
};

export const ModifiableList = ({
  name,
  type,
  field,
  listKey,
  append = false,
  label,
  lang,
  labelClassName,
  inputClassName,
  tooltipText,
  visible = true,
  ...props
}: ComponentProps<typeof Text> & {
  listKey: string;
  append?: boolean;
  lang?: SupportedLocale;
  labelClassName?: string;
  inputClassName?: string;
  tooltipText?: string;
  visible?: boolean;
}) => {
  const form = useFormContext();
  const { t } = useTranslation();
  const { user } = useAuth();
  const isReader = user?.role === "reader" ? true : false;

  const [open, setOpen] = useState(false);

  if (!visible) {
    return null;
  }

  return (
    <div className="flex flex-col w-full items-start">
      <div className="flex flex-row items-center gap-2 mb-1.5">
        <label
          className={cn(
            "flex text-start  text-slate-700 text-sm font-medium font-['Raleway'] leading-normal",
            labelClassName,
          )}
          htmlFor={name}
        >
          {label ?? t(name)}
        </label>
        {tooltipText && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger
                className="bg-transparent"
                onClick={(e) => e.preventDefault()}
              >
                <QuestionMarkCircledIcon className="w-4 h-4 text-gray-500 hover:text-gray-700 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="text-center text-black">
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
      <FormField
        defaultValue=""
        name={name}
        {...form}
        {...field}
        render={({ field }) => (
          <div className="flex flex-row gap-4 w-full">
            <HeadlessFormItem>
              <FormControl>
                <Input
                  id={name}
                  type={type ?? "text"}
                  {...field}
                  {...props}
                  className={cn(inputClassName)}
                />
              </FormControl>
            </HeadlessFormItem>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger disabled={isReader}>
                <PenIcon
                  className={cn(
                    "size-4",
                    isReader && "opacity-50 cursor-default",
                  )}
                />
              </SheetTrigger>

              <SheetContent className="w-[500px] px-0 max-h-lvh">
                <ModifiableListContent
                  name={label ?? t(name)}
                  listKey={listKey}
                  onSelectValue={(value) => {
                    const newValue = append ? `${field.value} ${value}` : value;

                    field.onChange(newValue);
                    setOpen(false);
                  }}
                  lang={lang}
                />
              </SheetContent>
            </Sheet>
          </div>
        )}
      />
    </div>
  );
};
